import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Alert, Button, IconButton, MenuItem, Stack, TextField } from "@mui/material";
import { Add, Edit, Sync } from "@mui/icons-material";
import { FilterModal } from "@/components/FilterModal";
import { DataField } from "@/components/DataField";
import usePagination from "@/hooks/pagination";
import { useNotification } from "@/context/NotificationContext";
import { useCrud } from "@/hooks/useCrud";
import { DataTable } from "@/components/DataTable";
import { CrudModal } from "@/components/crud/CrudModal";
import { CrudToolbar } from "@/components/crud/CrudToolbar";
import { useLogger } from "@/hooks/useLogger";
import { useListaRemota } from "@/hooks/useListaRemota";
import { LogEventType } from "@/api/log.service";
import { useAuth } from "@/context/AuthContext";
import { formatDataBR, listPrazos, savePrazo, MESES, rotuloCompetencia, type Prazo } from "@/api/escala.service";

interface PrazoFormProps {
  values: Partial<Prazo>;
  setValues: Dispatch<SetStateAction<Partial<Prazo>>>;
  isEditing: boolean;
}

function PrazoForm({ values, setValues }: PrazoFormProps) {
  return (
    <>
      <Stack direction="row" spacing={2}>
        <TextField
          label="Ano"
          type="number"
          fullWidth
          required
          value={values.ano ?? ""}
          onChange={(e) => setValues((v) => ({ ...v, ano: Number(e.target.value) }))}
          slotProps={{ htmlInput: { min: 1, max: 32767, step: 1 } }}
        />
        <TextField
          select
          label="Mês"
          fullWidth
          required
          value={values.mes ?? ""}
          onChange={(e) => setValues((v) => ({ ...v, mes: Number(e.target.value) }))}
        >
          {MESES.map((nome, i) => (
            <MenuItem key={i + 1} value={i + 1}>{nome}</MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction="row" spacing={2}>
        <DataField
          label="Data Limite Inicial"
          fullWidth
          required
          value={values.inicio_janela ?? ""}
          onChange={(data) => setValues((v) => ({ ...v, inicio_janela: data }))}
        />
        <DataField
          label="Data Limite Final"
          fullWidth
          required
          value={values.fim_janela ?? ""}
          onChange={(data) => setValues((v) => ({ ...v, fim_janela: data }))}
        />
      </Stack>
    </>
  );
}

export function EscalaPrazosTab() {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { log } = useLogger();
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { open, editing, handleCreate, handleEdit, handleClose } = useCrud<Prazo>();

  const anoAtual = new Date().getFullYear();
  const [inputAno, setInputAno] = useState(String(anoAtual));
  const [ano, setAno] = useState(anoAtual);

  // Mês filtra em cima do que listar_prazos já trouxe pro ano — o Method só
  // aceita { ano } (04-contratos.md), então não vale round-trip nenhum.
  const [inputMes, setInputMes] = useState("");
  const [mesFiltro, setMesFiltro] = useState<number | null>(null);

  const { itens: prazos, carregouAlgumaVez, erro, recarregar } = useListaRemota(
    () => listPrazos(ano, clientId),
    [clientId, ano],
    "Erro ao carregar prazos",
  );

  const sorted = useMemo(() => [...prazos].sort((a, b) => a.mes - b.mes), [prazos]);
  const filtered = useMemo(
    () => (mesFiltro ? sorted.filter((p) => p.mes === mesFiltro) : sorted),
    [sorted, mesFiltro],
  );

  useEffect(() => {
    setTotal(filtered.length);
  }, [filtered, setTotal]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const activeFilterCount = (ano !== anoAtual ? 1 : 0) + (mesFiltro ? 1 : 0);

  const applyFilters = () => {
    setAno(Number(inputAno) || anoAtual);
    setMesFiltro(inputMes ? Number(inputMes) : null);
    setPage(0);
  };

  const handleClearFilters = () => {
    setInputAno(String(anoAtual));
    setAno(anoAtual);
    setInputMes("");
    setMesFiltro(null);
    setPage(0);
  };

  const onSave = async (values: Partial<Prazo>) => {
    if (!values.ano || !values.mes) {
      notify("Competência é obrigatória", "error");
      return;
    }
    if (!values.inicio_janela || !values.fim_janela) {
      notify("Informe a data limite inicial e a final", "error");
      return;
    }
    if (values.fim_janela < values.inicio_janela) {
      notify("A data limite final não pode ser antes da inicial", "error");
      return;
    }
    const duplicado = prazos.some((p) => p.ano === values.ano && p.mes === values.mes && p.id !== editing?.id);
    if (duplicado) {
      notify(`Já existe um prazo cadastrado para ${rotuloCompetencia(values.ano, values.mes)}`, "error");
      return;
    }

    try {
      const result = await savePrazo(
        {
          id: editing?.id,
          ano: values.ano,
          mes: values.mes,
          inicio_janela: values.inicio_janela,
          fim_janela: values.fim_janela,
        },
        clientId,
      );

      log(editing ? LogEventType.Updated : LogEventType.Created, result, "escala_prazo", result.id, "info");
      notify(`Prazo ${editing ? "atualizado" : "criado"}!`, "success");
      recarregar();
      handleClose();
    } catch (e: any) {
      notify(e?.message || "Erro ao salvar prazo", "error");
    }
  };

  if (!carregouAlgumaVez) return <Sync />;

  return (
    <>
      <CrudToolbar>
        <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
          <TextField
            label="Ano"
            type="number"
            size="small"
            fullWidth
            value={inputAno}
            onChange={(e) => setInputAno(e.target.value)}
          />
          <TextField
            select
            label="Mês"
            size="small"
            fullWidth
            value={inputMes}
            onChange={(e) => setInputMes(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            {MESES.map((nome, i) => (
              <MenuItem key={i + 1} value={i + 1}>{nome}</MenuItem>
            ))}
          </TextField>
        </FilterModal>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Adicionar prazo
        </Button>
      </CrudToolbar>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Não foi possível carregar os prazos de {ano}.
        </Alert>
      )}

      <DataTable
        data={paged}
        columns={[
          { key: "mes", label: "Competência", render: (p) => rotuloCompetencia(p.ano, p.mes) },
          { key: "inicio_janela", label: "Data Inicial para Confecção", render: (p) => formatDataBR(p.inicio_janela) },
          { key: "fim_janela", label: "Data Final para Confecção", render: (p) => formatDataBR(p.fim_janela) },
        ]}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={(prazo) => (
          <IconButton aria-label={`Editar prazo de ${rotuloCompetencia(prazo.ano, prazo.mes)}`} onClick={() => handleEdit(prazo)}>
            <Edit />
          </IconButton>
        )}
      />

      <CrudModal<Prazo>
        open={open}
        onClose={handleClose}
        title={editing ? "Editar prazo" : "Adicionar prazo"}
        initialData={editing}
        onSubmit={onSave}
        Form={PrazoForm}
        extraFormProps={{}}
      />
    </>
  );
}
